"use client";

import { motion } from "framer-motion";
import Image from "next/image";

// Brand logos
const brandLogos = [
  { name: "HUX Hotel", src: "/brands/Hux_light.png", height: 40, width: 150 },
  { name: "Just Toys Intl", src: "/brands/just-toys-intl-white.png", height: 60, width: 150 },
  { name: "FoxFlorest", src: "/brands/foxflorest-white.png", height: 80, width: 150 },
  { name: "Herbdon", src: "/brands/herbdon-white.png", height: 30, width: 150 },
  { name: "BussDown", src: "/brands/bdc-logo-black.png", height: 90, width: 150, invertOnDark: true },
  { name: "No Broma", src: "/brands/nobroma.png", height: 80, width: 150 },
  { name: "Windsurf Paradise", srcLight: "/brands/windsurf-paradise-black.png", srcDark: "/brands/windsurf-paradise-white.png", height: 80, width: 200, dualTheme: true },
  { name: "Desert City Stays", src: "/brands/desert-city-stays-dark.png", height: 180, width: 280, invertOnLight: true },
];

// AI models - increased size to match brand logos
const aiModels = [
  { name: "Midjourney", logo: "/logos/midjourney.svg", isText: false, size: 48 },
  { name: "OpenAI", logo: "/logos/openai.svg", isText: false, size: 48 },
  { name: "Runway", logo: "/logos/runway.png", isText: false, size: 48 },
  { name: "Google Veo 3", logo: null, isText: true, textLogo: "Veo3", size: 48 },
  { name: "Sora", logo: "/logos/sora-white.png", isText: false, size: 48 },
  { name: "Nano Banana", logo: "/logos/nanobana-white.png", isText: false, size: 48 },
  { name: "Wan", logo: "/logos/wan-white-black.png", isText: false, size: 48 },
  { name: "Kling", logo: "/logos/kling.png", isText: false, size: 48, makeWhite: true },
];

export function StatsSection() {
  return (
    <section id="featured" className="relative bg-background" style={{ paddingTop: 'clamp(5rem, 12vh, 8rem)', paddingBottom: 'clamp(5rem, 12vh, 8rem)' }}>
      {/* Gradient background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[1200px] h-[600px]"
          style={{
            background: `
              radial-gradient(ellipse 70% 50% at 50% 50%, rgba(16, 185, 129, 0.25) 0%, transparent 60%),
              radial-gradient(ellipse 50% 40% at 30% 40%, rgba(163, 230, 53, 0.15) 0%, transparent 50%),
              radial-gradient(ellipse 50% 40% at 70% 60%, rgba(5, 150, 105, 0.2) 0%, transparent 50%)
            `,
            filter: "blur(40px)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Top Marquee - Brands scrolling LEFT */}
        <div className="mb-12 md:mb-16">
          <div
            className="relative overflow-hidden mx-auto"
            style={{
              maxWidth: "1200px",
              maskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            }}
          >
            <div className="flex gap-12 marquee-row-left-faster">
              {[...Array(4)].flatMap(() => brandLogos).map((brand, index) => (
                <div
                  key={`brand-${brand.name}-${index}`}
                  className="shrink-0 flex items-center justify-center"
                  style={{ height: 100 }}
                >
                  <div style={{ height: brand.height }}>
                    {brand.dualTheme ? (
                      <>
                        <Image
                          src={brand.srcLight}
                          alt={brand.name}
                          width={brand.width}
                          height={brand.height}
                          className="h-full w-auto object-contain logo-themed block dark:hidden"
                        />
                        <Image
                          src={brand.srcDark}
                          alt={brand.name}
                          width={brand.width}
                          height={brand.height}
                          className="h-full w-auto object-contain logo-themed hidden dark:block"
                        />
                      </>
                    ) : (
                      <Image
                        src={brand.src || ''}
                        alt={brand.name}
                        width={brand.width}
                        height={brand.height}
                        className={`h-full w-auto object-contain logo-themed ${brand.invertOnDark ? 'dark:invert' : ''} ${brand.invertOnLight ? 'invert dark:invert-0' : ''}`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Center Header Section */}
        <div className="max-w-5xl mx-auto px-6 mb-12 md:mb-16">
          {/* Top Line */}
          <div className="h-[2.5px] bg-foreground/20 mb-8 md:mb-10"></div>

          {/* Header with Drop Cap & */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-4 md:gap-6"
          >
            {/* Big & - Drop Cap Style */}
            <div
              className="text-gradient font-bold leading-none flex-shrink-0"
              style={{ fontSize: 'clamp(3.5rem, 8vw + 1.5rem, 7rem)' }}
            >
              &
            </div>

            {/* Text Content */}
            <div className="text-left">
              {/* Desktop Text */}
              <div className="hidden md:block">
                <div
                  className="font-bold text-foreground leading-tight"
                  style={{ fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)' }}
                >
                  <span className="block">Trusted by <span className="text-gradient">growing brands</span></span>
                </div>
                <div
                  className="font-bold text-foreground leading-tight mt-1"
                  style={{ fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)' }}
                >
                  <span className="block">Powered by <span className="text-gradient">leading models</span></span>
                </div>
              </div>

              {/* Mobile Text */}
              <div className="block md:hidden">
                <div
                  className="font-bold text-foreground leading-tight"
                  style={{ fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)' }}
                >
                  <span className="block">Trusted by <span className="text-gradient">brands</span></span>
                </div>
                <div
                  className="font-bold text-foreground leading-tight mt-1"
                  style={{ fontSize: 'clamp(1.5rem, 3vw + 0.5rem, 2.5rem)' }}
                >
                  <span className="block">Powered by <span className="text-gradient">AI</span></span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bottom Line */}
          <div className="h-[2.5px] bg-foreground/20 mt-8 md:mt-10"></div>
        </div>

        {/* Bottom Marquee - AI Models scrolling RIGHT */}
        <div>
          <div
            className="relative overflow-hidden mx-auto"
            style={{
              maxWidth: "1200px",
              maskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            }}
          >
            <div className="flex gap-12 marquee-row-right-faster">
              {[...Array(4)].flatMap(() => aiModels).map((model, index) => (
                <div
                  key={`model-${model.name}-${index}`}
                  className="shrink-0 flex items-center gap-3"
                  style={{ height: 100 }}
                >
                  {model.isText ? (
                    <span className="text-foreground font-bold text-lg md:text-xl">{model.textLogo}</span>
                  ) : (
                    <div className="relative flex items-center justify-center" style={{ width: model.size, height: model.size }}>
                      <Image
                        src={model.logo!}
                        alt={model.name}
                        width={model.size}
                        height={model.size}
                        className="object-contain logo-themed"
                      />
                    </div>
                  )}
                  <span className="text-foreground font-semibold text-base md:text-xl whitespace-nowrap">{model.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
